import React, { useState } from "react";
import Logo from "../assets/SVGs/Logo";
import {
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
} from "@mui/material";
import VisibilityIcon from "../assets/SVGs/Login/VisibilityIcon";
import VisibilityOffIcon from "../assets/SVGs/Login/VisibilityOffIcon";
import { useSearchParams, useNavigate } from "react-router-dom";
import { LoginAPI } from "../api/login";
import { hashPassword } from "../componets/helper";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  const email = searchParams.get("email") || "";

  const [form, setForm] = useState({
    password: "",
    confirmPassword: "",
  });

  const [formError, setFormError] = useState({
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleError = (formObj = form) => {
    const temp = { ...formError };

    temp.password = formObj.password ? "" : "Password is required!";

    if (!formObj.confirmPassword) {
      temp.confirmPassword = "Password confirmation is required!";
    } else if (formObj.confirmPassword !== formObj.password) {
      temp.confirmPassword = "Passwords do not match!";
    } else {
      temp.confirmPassword = "";
    }

    setFormError({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleChange = (key, value) => {
    const updated = { ...form, [key]: value };
    setForm(updated);
    handleError(updated);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSubmit(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!handleError()) return;

    if (!token) {
      setSubmitError("The reset link is invalid or has expired.");
      return;
    }

    setIsLoading(true);
    setSubmitSuccess(false);
    setSubmitError("");

    try {
      const hashedPassword = await hashPassword(form.password);

      await LoginAPI.reset_password({
        token,
        email,
        new_password: hashedPassword,
      });

      setSubmitSuccess(true);
    } catch (error) {
      console.error(error);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleClickShowConfirmPassword = () =>
    setShowConfirmPassword((show) => !show);

  return (
    <div className="flex align-center justify-center login-container ">
      <div className="form-container flex align-center justify-center flex-col">
        <Logo />

        {!submitSuccess ? (
          <>
            <div className="flex align-center justify-center flex-col text-container">
              <h2>Reset your password</h2>
              <p>Enter your new password</p>
            </div>

            <div className="text-fleld-container flex flex-col">
              {/* Email */}
              <TextField
                margin="normal"
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                value={email}
                className="login-textField "
                size="small"
                InputProps={{
                  readOnly: true,
                }}
              />

              {/* New password */}
              <FormControl
                variant="outlined"
                className="login-textField"
                sx={{ marginTop: "16px" }}
                size="small"
                error={formError.password !== ""}
              >
                <InputLabel htmlFor="outlined-new-password">
                  New password*
                </InputLabel>
                <OutlinedInput
                  margin="normal"
                  required
                  fullWidth
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon className="icon-color" />
                        ) : (
                          <VisibilityIcon className="icon-color" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="New password"
                  value={form.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  className="login-textField "
                  onKeyUp={handleKeyPress}
                />
                {formError.password !== "" && (
                  <FormHelperText>{formError.password}</FormHelperText>
                )}
              </FormControl>

              {/* Confirm password */}
              <FormControl
                variant="outlined"
                className="login-textField"
                sx={{ marginTop: "16px" }}
                size="small"
                error={formError.confirmPassword !== ""}
              >
                <InputLabel htmlFor="outlined-confirm-password">
                  Confirm password*
                </InputLabel>
                <OutlinedInput
                  margin="normal"
                  required
                  fullWidth
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleClickShowConfirmPassword}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseDownPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? (
                          <VisibilityOffIcon className="icon-color" />
                        ) : (
                          <VisibilityIcon className="icon-color" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  }
                  label="Confirm password"
                  value={form.confirmPassword}
                  onChange={(e) =>
                    handleChange("confirmPassword", e.target.value)
                  }
                  className="login-textField "
                  onKeyUp={handleKeyPress}
                />
                {formError.confirmPassword !== "" && (
                  <FormHelperText>{formError.confirmPassword}</FormHelperText>
                )}
              </FormControl>
            </div>

            <Button
              type="submit"
              className="login-btn flex align-center justify-center"
              variant="contained"
              color="primary"
              disabled={isLoading}
              onClick={handleSubmit}
            >
              {isLoading ? (
                <CircularProgress
                  sx={{
                    color: "#fff",
                    width: "20px!important",
                    height: "20px!important",
                  }}
                  disableShrink
                  variant="indeterminate"
                />
              ) : (
                "Reset password"
              )}
            </Button>

            {submitError && <p className="error-message">{submitError}</p>}
          </>
        ) : (
          <div className="flex align-center justify-center flex-col text-container">
            <h2>Password reset successful</h2>
            <p>You can now sign in with your new password.</p>
            <Button
              className="login-btn flex align-center justify-center mt-4"
              variant="contained"
              color="primary"
              onClick={() => navigate("/login")}
            >
              Back to login
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
